/**
 * Points of sale — the places that host depots.
 *
 * A place is not a stock: it is a name, an address, and what the operations
 * team keeps about it (surface, lifts, headcount). The same place can host
 * the depots of several companies, which is the whole reason it has a screen
 * of its own rather than being a column on a depot.
 *
 * The code is optional when creating: the server derives it from the name the
 * same way the structure import does, so a place typed here today and the
 * same place arriving in a file tomorrow land on one row.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { quantity } from "@hardauto/core-i18n";
import { Button, DataTable, Input, useToast, type Column } from "@hardauto/core-ui";

import type { PointOfSale, PointOfSaleInput, ReferentialApi, Site } from "../../api";
import { NAMESPACE } from "../../i18n";
import { DeleteDialog, type DeleteTarget } from "./DeleteDialog";
import { VisibilityControls } from "./VisibilityControls";

export interface PointsOfSaleTabProps {
  api: ReferentialApi;
  points: readonly PointOfSale[];
  /** The depots, to say how many a deletion would take. */
  sites?: readonly Site[];
  loading: boolean;
  failed: boolean;
  reload: () => void;
  mayWrite: boolean;
}

interface Draft {
  code: string;
  name: string;
  city: string;
  address: string;
  specialisation: string;
  surface: string;
  lifts: string;
  headcount: string;
}

const EMPTY: Draft = {
  code: "",
  name: "",
  city: "",
  address: "",
  specialisation: "",
  surface: "",
  lifts: "",
  headcount: "",
};

function draftOf(point: PointOfSale): Draft {
  return {
    code: point.code,
    name: point.name,
    city: point.city,
    address: point.address,
    specialisation: point.specialisation,
    surface: point.surface_m2 ?? "",
    lifts: point.lift_count === null ? "" : String(point.lift_count),
    headcount: point.headcount === null ? "" : String(point.headcount),
  };
}

/** A whole number, or `null` for an empty or unreadable field. */
function count(text: string): number | null {
  const parsed = Number.parseInt(text.trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** A decimal as the API takes it — a string, with a dot — or `null`. */
function decimal(text: string): string | null {
  const normalised = text.trim().replace(",", ".");
  return normalised && Number.isFinite(Number(normalised)) ? normalised : null;
}

function bodyOf(draft: Draft): PointOfSaleInput {
  return {
    name: draft.name.trim(),
    city: draft.city.trim(),
    address: draft.address.trim(),
    specialisation: draft.specialisation.trim(),
    surface_m2: decimal(draft.surface),
    lift_count: count(draft.lifts),
    headcount: count(draft.headcount),
  };
}

export function PointsOfSaleTab({
  api,
  points,
  sites = [],
  loading,
  failed,
  reload,
  mayWrite,
}: PointsOfSaleTabProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);

  function askDelete(row: PointOfSale) {
    const depots = sites.filter((site) => site.point_of_sale_code === row.code).length;
    setDeleting({
      title: t("deletion.title.point", { name: row.name, code: row.code }),
      consequences: depots > 0 ? [t("deletion.will.depots", { count: depots })] : [],
      run: () => api.deletePointOfSale(row.code),
    });
  }

  // `null` when no form is open; `""` for a new place; a code when editing.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);

  function set(field: keyof Draft, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  const figure = (value: string | number | null) =>
    value === null || value === "" ? (
      "—"
    ) : (
      <span className="hardauto-num">{quantity(String(value), i18n.language)}</span>
    );

  const columns: Column<PointOfSale>[] = [
    { key: "city", header: t("structure.points.column.city"), render: (row) => row.city || "—" },
    { key: "name", header: t("structure.points.column.name"), render: (row) => row.name },
    {
      key: "code",
      header: t("structure.points.column.code"),
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "address",
      header: t("structure.points.column.address"),
      render: (row) => row.address || "—",
      secondary: true,
    },
    {
      key: "specialisation",
      header: t("structure.points.column.specialisation"),
      render: (row) => row.specialisation || "—",
      secondary: true,
    },
    {
      key: "surface",
      header: t("structure.points.column.surface"),
      render: (row) => figure(row.surface_m2),
      align: "end",
    },
    {
      key: "lifts",
      header: t("structure.points.column.lifts"),
      render: (row) => figure(row.lift_count),
      align: "end",
    },
    {
      key: "headcount",
      header: t("structure.points.column.headcount"),
      render: (row) => figure(row.headcount),
      align: "end",
    },
    {
      key: "state",
      header: t("structure.points.column.state"),
      render: (row) => (
        <div className="hardauto-structure__inline">
          <VisibilityControls
            customers={{
              visible: row.is_active,
              hiddenByParent: row.hidden_by_parent_customers ?? false,
            }}
            backOffice={{
              visible: row.back_office_visible,
              hiddenByParent: row.hidden_by_parent_back_office ?? false,
            }}
            parent="depots"
            editable={mayWrite}
            isBusy={(audience) => action.isBusy(`toggle:${audience}:${row.code}`)}
            onToggle={(audience, visible) =>
              void action.run(`toggle:${audience}:${row.code}`, async () => {
                try {
                  await api.updatePointOfSale(
                    row.code,
                    audience === "customers"
                      ? { is_active: visible }
                      : { back_office_visible: visible },
                  );
                  reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          />
          {mayWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(row.code);
                  setDraft(draftOf(row));
                }}
              >
                {t("structure.edit")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => askDelete(row)}>
                {t("deletion.action")}
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  const field = (name: keyof Draft, label: string, required = false) => (
    <div className="hardauto-inline-form__field">
      <Field label={label} required={required}>
        {(props) => (
          <Input
            {...props}
            value={draft[name]}
            onChange={(event) => set(name, event.target.value)}
          />
        )}
      </Field>
    </div>
  );

  return (
    <div className="hardauto-structure__panel">
      {mayWrite && editing === null ? (
        <div>
          <Button
            variant="primary"
            onClick={() => {
              setEditing("");
              setDraft(EMPTY);
            }}
          >
            {t("structure.points.create")}
          </Button>
        </div>
      ) : null}

      {editing !== null ? (
        <form
          className="hardauto-inline-form"
          aria-label={
            editing
              ? t("structure.points.editTitle", { code: editing })
              : t("structure.points.create")
          }
          onSubmit={(event) => {
            event.preventDefault();
            if (!draft.name.trim()) return;
            void action.run("save", async () => {
              try {
                if (editing) {
                  await api.updatePointOfSale(editing, bodyOf(draft));
                } else {
                  await api.createPointOfSale({ ...bodyOf(draft), code: draft.code.trim() });
                }
                setEditing(null);
                reload();
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            });
          }}
        >
          {field("name", t("structure.points.column.name"), true)}
          {editing ? null : field("code", t("structure.points.newCode"))}
          {field("city", t("structure.points.column.city"))}
          {field("address", t("structure.points.column.address"))}
          {field("specialisation", t("structure.points.column.specialisation"))}
          {field("surface", t("structure.points.column.surface"))}
          {field("lifts", t("structure.points.column.lifts"))}
          {field("headcount", t("structure.points.column.headcount"))}
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("save")}
              busyLabel={t("structure.save")}
            >
              {editing ? t("structure.save") : t("structure.create")}
            </Button>
            <Button onClick={() => setEditing(null)}>{t("structure.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <DeleteDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />

      <DataTable<PointOfSale>
        caption={t("structure.points.caption")}
        columns={columns}
        rows={points}
        rowKey={(row) => row.code}
        loading={loading}
        loadingLabel={t("structure.points.loading")}
        error={
          failed
            ? { title: t("error.title"), onRetry: reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{
          title: t("structure.points.empty.title"),
          description: t("structure.points.empty.body"),
        }}
      />
    </div>
  );
}
